import { RotateCw, X, ZoomIn, ZoomOut } from 'lucide-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Button from './Button'

/**
 * Props interface for the ImageDialog component
 */
export interface ImageDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean
  /** URL of the image to display */
  imageUrl: string
  /** Alt text for the image */
  imageAlt: string
  /** Callback when dialog should close */
  onClose: () => void
  /** Optional image title */
  title?: string
  /** Additional CSS classes */
  className?: string
}

/**
 * ImageDialog component for full-screen image viewing with zoom and pan capabilities
 *
 * Features:
 * - Full-screen overlay with backdrop blur
 * - Pinch-to-zoom and scroll gestures on mobile
 * - Double-tap to zoom in/out on mobile
 * - Zoom controls for desktop
 * - Keyboard navigation (ESC to close, +/- to zoom)
 * - Accessible with proper ARIA attributes
 * - Smooth animations and transitions
 * - Touch-friendly controls optimized for mobile
 */
export const ImageDialog: React.FC<ImageDialogProps> = ({
  isOpen,
  imageUrl,
  imageAlt,
  onClose,
  title,
  className = '',
}) => {
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [panX, setPanX] = useState(0)
  const [panY, setPanY] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })

  // Touch gesture state
  const [lastTouchDistance, setLastTouchDistance] = useState(0)
  const [lastTouchCenter, setLastTouchCenter] = useState({ x: 0, y: 0 })
  const [lastTapTime, setLastTapTime] = useState(0)
  const imageContainerRef = useRef<HTMLDivElement>(null)

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setZoom(1)
      setRotation(0)
      setPanX(0)
      setPanY(0)
      setIsDragging(false)
      setLastTouchDistance(0)
      setLastTouchCenter({ x: 0, y: 0 })
      setLastTapTime(0)
      // Prevent body scroll when dialog is open
      document.body.style.overflow = 'hidden'
    } else {
      // Restore body scroll when dialog closes
      document.body.style.overflow = 'unset'
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  /**
   * Calculate distance between two touch points
   */
  const getTouchDistance = (
    touch1: React.Touch,
    touch2: React.Touch
  ): number => {
    const dx = touch1.clientX - touch2.clientX
    const dy = touch1.clientY - touch2.clientY
    return Math.sqrt(dx * dx + dy * dy)
  }

  /**
   * Calculate center point between two touches
   */
  const getTouchCenter = (touch1: React.Touch, touch2: React.Touch) => ({
    x: (touch1.clientX + touch2.clientX) / 2,
    y: (touch1.clientY + touch2.clientY) / 2,
  })

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen) return

      switch (event.key) {
        case 'Escape':
          onClose()
          break
        case '+':
        case '=':
          event.preventDefault()
          setZoom(prev => Math.min(prev + 0.25, 5))
          break
        case '-':
          event.preventDefault()
          setZoom(prev => Math.max(prev - 0.25, 0.25))
          break
        case 'r':
        case 'R':
          event.preventDefault()
          setRotation(prev => (prev + 90) % 360)
          break
        case '0':
          event.preventDefault()
          setZoom(1)
          setPanX(0)
          setPanY(0)
          setRotation(0)
          break
      }
    },
    [isOpen, onClose]
  )

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, handleKeyDown])

  /**
   * Handle zoom controls
   */
  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 5))
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.25))
  const handleRotate = () => setRotation(prev => (prev + 90) % 360)
  const handleReset = () => {
    setZoom(1)
    setPanX(0)
    setPanY(0)
    setRotation(0)
  }

  /**
   * Handle mouse events for desktop
   */
  const handleMouseDown = (event: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true)
      setDragStart({ x: event.clientX - panX, y: event.clientY - panY })
    }
  }

  const handleMouseMove = (event: React.MouseEvent) => {
    if (isDragging && zoom > 1) {
      setPanX(event.clientX - dragStart.x)
      setPanY(event.clientY - dragStart.y)
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  /**
   * Handle touch events for mobile
   */
  const handleTouchStart = (event: React.TouchEvent) => {
    event.preventDefault() // Prevent default browser behavior

    const touches = event.touches

    if (touches.length === 1) {
      // Single touch - check for double tap or start panning
      const touch = touches[0]
      const currentTime = Date.now()

      // Double tap detection (within 300ms)
      if (currentTime - lastTapTime < 300) {
        // Double tap - toggle zoom
        if (zoom === 1) {
          setZoom(2)
        } else {
          handleReset()
        }
        setLastTapTime(0) // Reset to prevent triple tap
      } else {
        setLastTapTime(currentTime)
        // Start panning if zoomed
        if (zoom > 1) {
          setIsDragging(true)
          setDragStart({
            x: touch.clientX - panX,
            y: touch.clientY - panY,
          })
        }
      }
    } else if (touches.length === 2) {
      // Two finger touch - start pinch zoom
      const touch1 = touches[0]
      const touch2 = touches[1]

      const distance = getTouchDistance(touch1, touch2)
      const center = getTouchCenter(touch1, touch2)

      setLastTouchDistance(distance)
      setLastTouchCenter(center)
      setIsDragging(false) // Stop panning during pinch
    }
  }

  const handleTouchMove = (event: React.TouchEvent) => {
    event.preventDefault() // Prevent default browser behavior

    const touches = event.touches

    if (touches.length === 1 && isDragging && zoom > 1) {
      // Single touch panning
      const touch = touches[0]
      setPanX(touch.clientX - dragStart.x)
      setPanY(touch.clientY - dragStart.y)
    } else if (touches.length === 2) {
      // Two finger pinch zoom
      const touch1 = touches[0]
      const touch2 = touches[1]

      const distance = getTouchDistance(touch1, touch2)
      const center = getTouchCenter(touch1, touch2)

      if (lastTouchDistance > 0) {
        // Calculate zoom change
        const zoomChange = distance / lastTouchDistance
        const newZoom = Math.min(Math.max(zoom * zoomChange, 0.25), 5)

        // Calculate pan adjustment to keep zoom centered
        const containerRect = imageContainerRef.current?.getBoundingClientRect()
        if (containerRect) {
          const centerX = containerRect.left + containerRect.width / 2
          const centerY = containerRect.top + containerRect.height / 2

          // Adjust pan to zoom towards touch center
          const deltaX = (center.x - centerX) * (zoomChange - 1)
          const deltaY = (center.y - centerY) * (zoomChange - 1)

          setPanX(prev => prev - deltaX)
          setPanY(prev => prev - deltaY)
        }

        setZoom(newZoom)
      }

      setLastTouchDistance(distance)
      setLastTouchCenter(center)
    }
  }

  const handleTouchEnd = () => {
    setIsDragging(false)
    setLastTouchDistance(0)
  }

  /**
   * Handle backdrop click to close
   */
  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose()
    }
  }

  if (!isOpen) return null

  // Render the dialog using createPortal to ensure it's rendered at document level
  return createPortal(
    <div
      className={`image-dialog ${className}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'image-dialog-title' : undefined}
      aria-describedby="image-dialog-description"
    >
      {/* Backdrop */}
      <div
        className="image-dialog__backdrop"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Dialog Content */}
      <div className="image-dialog__content">
        {/* Header with title and close button */}
        <div className="image-dialog__header">
          {title && (
            <h2 id="image-dialog-title" className="image-dialog__title">
              {title}
            </h2>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="image-dialog__close"
            aria-label="Close image dialog"
          >
            <X size={20} />
          </Button>
        </div>

        {/* Image Container */}
        <div
          ref={imageContainerRef}
          className="image-dialog__image-container"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{
            cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
          }}
        >
          <img
            src={imageUrl}
            alt={imageAlt}
            className="image-dialog__image"
            style={{
              transform: `translate(${panX}px, ${panY}px) scale(${zoom}) rotate(${rotation}deg)`,
              transformOrigin: 'center center',
            }}
            draggable={false}
            onError={e => {
              console.error('Image failed to load:', imageUrl, e)
            }}
            onLoad={() => {
              console.log('Image loaded successfully:', imageUrl)
            }}
          />
        </div>

        {/* Controls */}
        <div className="image-dialog__controls">
          <div className="image-dialog__zoom-controls">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleZoomOut}
              disabled={zoom <= 0.25}
              aria-label="Zoom out"
            >
              <ZoomOut size={18} />
            </Button>
            <span className="image-dialog__zoom-level">
              {Math.round(zoom * 100)}%
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleZoomIn}
              disabled={zoom >= 5}
              aria-label="Zoom in"
            >
              <ZoomIn size={18} />
            </Button>
          </div>

          <div className="image-dialog__other-controls">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRotate}
              aria-label="Rotate image"
            >
              <RotateCw size={18} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              aria-label="Reset zoom and position"
            >
              Reset
            </Button>
          </div>
        </div>

        {/* Keyboard shortcuts help */}
        <div className="image-dialog__help">
          <p id="image-dialog-description" className="image-dialog__help-text">
            Pinch to zoom, double-tap to toggle zoom, drag to pan. Use +/- to
            zoom, R to rotate, 0 to reset, ESC to close
          </p>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default ImageDialog
